import { baseApi } from './baseApi';

// These responses are not passed through normalizeKeys. A workflow document and a
// step's input and output are data whose keys must stay exactly as written, so the
// whole workflow API keeps the server's snake_case.

export type WorkflowLifecycle = 'draft' | 'active' | 'paused' | 'archived';
export type RunStatus =
  | 'queued'
  | 'running'
  | 'waiting'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export interface WorkflowDocument {
  schema_version: number;
  name: string;
  title?: string;
  description: string;
  input?: {
    description?: string;
    schema?: Record<string, unknown>;
    attachments?: { label?: string; min?: number; max?: number };
  };
  trigger: {
    kind: 'api' | 'schedule' | 'event';
    schedule?: { cron: string; timezone: string; display?: string; overlap?: string };
    event?: { name: string };
  };
  limits?: Record<string, number>;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

export interface WorkflowNode {
  id: string;
  type: 'trigger' | 'agent' | 'decision' | 'end';
  title?: string;
  purpose?: string;
  agent?: { name: string; version?: string } | null;
  input?: {
    description?: string;
    instruction?: string;
    slots?: Record<string, { from: string; required?: boolean }>;
  };
  output?: { description?: string; format?: string };
  wait?: {
    kind: 'event' | 'approval';
    event?: string;
    prompt?: string;
    when: string;
    on_reject?: string;
    on_timeout?: string;
    timeout_seconds?: number;
  };
  decision?: { mode: 'rule' | 'agent'; question: string; agent?: { name: string } | null };
  outcome?: string;
}

export interface WorkflowEdge {
  from: string;
  to: string;
  when?: string;
  loop?: boolean;
  max_passes?: number;
}

export interface WorkflowSummary {
  id: string;
  name: string;
  title: string | null;
  description: string;
  lifecycle: WorkflowLifecycle;
  trigger_kind: 'api' | 'schedule' | 'event';
  agent_count: number;
  active_version: number | null;
  updated_at: string;
}

export interface ReadinessIssue {
  layer: number;
  code: string;
  message: string;
  next: string;
  node_id: string | null;
  severity: string;
}

export interface WorkflowVersionInfo {
  version: number;
  state: string;
  version_id: string;
  content_hash: string;
  saved_by: string;
  save_source: string;
  saved_at: string;
}

export interface WorkflowDetail extends WorkflowSummary {
  draft: WorkflowVersionInfo | null;
  live: WorkflowVersionInfo | null;
  draft_ahead: boolean;
  definition: WorkflowDocument | null;
  active_definition: WorkflowDocument | null;
  content_hash: string | null;
  applications: { app_id: string; name: string }[];
  readiness?: { ready: boolean; issues: ReadinessIssue[]; warnings: ReadinessIssue[] };
}

export interface RunSummary {
  id: string;
  workflow_id: string;
  kind: 'test' | 'live';
  status: RunStatus;
  outcome: string | null;
  version: number | null;
  application: string | null;
  trigger: { kind: string };
  started_by: { kind: string; id: string };
  stop_reason: string | null;
  error: string | null;
  next: string | null;
  cancel_requested: boolean;
  queued_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface RunPass {
  pass_no: number;
  attempt: number;
  status: string;
  agent_name: string | null;
  agent_version: string | null;
  exchange_id: string | null;
  session_id: string | null;
  input: unknown;
  output: unknown;
  wait_payload: unknown;
  branch: string | null;
  error_code: string | null;
  error: string | null;
  next: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface RunStep {
  id: string;
  type: string;
  title: string | null;
  status: string;
  taken: boolean;
  reason?: string;
  passes: RunPass[];
}

export interface RunDetail extends RunSummary {
  input: unknown;
  attachments: string[];
  result: unknown;
  definition: WorkflowDocument;
  events_channel: string;
  steps: RunStep[];
  usage: {
    input_tokens: number;
    output_tokens: number;
    reasoning_tokens: number;
    model: string | null;
    platform_cost_usd: number;
  };
  events: { seq: number; event_type: string; node_id: string | null; at: string }[];
}

export interface BuilderTurn {
  id: string;
  conversation_id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  reply: string | null;
  proposal: WorkflowDocument | null;
  issues: ReadinessIssue[];
  repaired: boolean;
  suggest_test_run: boolean;
  warnings: string[];
  error: string | null;
}

export interface WorkflowVersionRow {
  version_id: string;
  content_hash: string;
  saved_by: string | null;
  save_source: string | null;
  saved_at: string;
  state: string;
  version: number | null;
}

export const workflowsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWorkflows: builder.query<WorkflowSummary[], string>({
      // The org id is cache-key only. The tenant is sent as x-organization-id.
      query: () => '/portal/workflows',
      transformResponse: (response: { workflows: WorkflowSummary[] }) =>
        response.workflows ?? [],
      providesTags: ['Workflow'],
    }),
    getWorkflow: builder.query<WorkflowDetail, string>({
      query: (id) => `/portal/workflows/${id}`,
      providesTags: ['Workflow'],
    }),
    createWorkflow: builder.mutation<
      WorkflowDetail,
      { document: WorkflowDocument; source?: 'builder' }
    >({
      query: (body) => ({ url: '/portal/workflows', method: 'POST', body }),
      invalidatesTags: ['Workflow'],
    }),
    saveWorkflowDraft: builder.mutation<
      WorkflowDetail,
      { id: string; document: WorkflowDocument; base_hash: string; source?: 'builder' }
    >({
      query: ({ id, ...body }) => ({
        url: `/portal/workflows/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Workflow'],
    }),
    setWorkflowStatus: builder.mutation<
      WorkflowDetail,
      { id: string; action: string; content_hash?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/portal/workflows/${id}/status`,
        method: 'POST',
        body: { ...body, confirm: true },
      }),
      invalidatesTags: ['Workflow'],
    }),
    discardWorkflowDraft: builder.mutation<
      WorkflowDetail & { discarded_version_id: string },
      string
    >({
      query: (id) => ({ url: `/portal/workflows/${id}/draft`, method: 'DELETE' }),
      invalidatesTags: ['Workflow'],
    }),
    getWorkflowVersions: builder.query<{ versions: WorkflowVersionRow[] }, string>({
      query: (id) => `/portal/workflows/${id}/versions`,
      providesTags: ['Workflow'],
    }),
    restoreWorkflowVersion: builder.mutation<
      WorkflowDetail,
      { id: string; versionId: string }
    >({
      query: ({ id, versionId }) => ({
        url: `/portal/workflows/${id}/versions/${encodeURIComponent(versionId)}/restore`,
        method: 'POST',
      }),
      invalidatesTags: ['Workflow'],
    }),
    getWorkflowRuns: builder.query<RunSummary[], string>({
      query: (id) => `/portal/workflows/${id}/runs`,
      transformResponse: (response: { runs: RunSummary[] }) => response.runs ?? [],
      providesTags: ['WorkflowRun'],
    }),
    getWorkflowRun: builder.query<RunDetail, { id: string; runId: string }>({
      query: ({ id, runId }) => `/portal/workflows/${id}/runs/${runId}`,
      providesTags: ['WorkflowRun'],
    }),
    startWorkflowRun: builder.mutation<
      RunSummary,
      { id: string; kind: 'test' | 'live'; input?: unknown; attachments?: string[] }
    >({
      query: ({ id, ...body }) => ({
        url: `/portal/workflows/${id}/runs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WorkflowRun'],
    }),
    cancelWorkflowRun: builder.mutation<RunSummary, { id: string; runId: string }>({
      query: ({ id, runId }) => ({
        url: `/portal/workflows/${id}/runs/${runId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['WorkflowRun'],
    }),
    respondToWorkflowRun: builder.mutation<
      RunSummary,
      { id: string; runId: string; answer: 'approve' | 'reject' | 'reply'; payload?: object }
    >({
      query: ({ id, runId, ...body }) => ({
        url: `/portal/workflows/${id}/runs/${runId}/respond`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WorkflowRun'],
    }),
    askWorkflowRun: builder.mutation<
      { answer: string; steps: string[]; refused?: boolean },
      { id: string; runId: string; question: string }
    >({
      query: ({ id, runId, question }) => ({
        url: `/portal/workflows/${id}/runs/${runId}/ask`,
        method: 'POST',
        body: { question },
      }),
    }),
    sendBuilderMessage: builder.mutation<
      BuilderTurn,
      {
        message: string;
        conversation_id?: string;
        workflow_id?: string;
        document?: WorkflowDocument;
        focus_node?: string;
        chosen_agents?: string[];
      }
    >({
      query: (body) => ({
        url: '/portal/workflows/builder/messages',
        method: 'POST',
        body,
      }),
    }),
    getBuilderTurn: builder.query<BuilderTurn, string>({
      query: (turnId) => `/portal/workflows/builder/turns/${turnId}`,
    }),
    getApplicationWorkflows: builder.query<WorkflowSummary[], string>({
      query: (appId) => `/portal/applications/${appId}/workflows`,
      transformResponse: (response: { workflows: WorkflowSummary[] }) =>
        response.workflows ?? [],
      providesTags: ['Workflow'],
    }),
    addApplicationWorkflow: builder.mutation<
      { workflows: WorkflowSummary[] },
      { appId: string; workflowId: string }
    >({
      query: ({ appId, workflowId }) => ({
        url: `/portal/applications/${appId}/workflows`,
        method: 'POST',
        body: { workflow_id: workflowId },
      }),
      invalidatesTags: ['Workflow'],
    }),
    removeApplicationWorkflow: builder.mutation<
      { workflows: WorkflowSummary[] },
      { appId: string; workflowId: string }
    >({
      query: ({ appId, workflowId }) => ({
        url: `/portal/applications/${appId}/workflows/${workflowId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Workflow'],
    }),
  }),
});

export const {
  useGetWorkflowsQuery,
  useGetWorkflowQuery,
  useCreateWorkflowMutation,
  useSaveWorkflowDraftMutation,
  useSetWorkflowStatusMutation,
  useDiscardWorkflowDraftMutation,
  useGetWorkflowVersionsQuery,
  useRestoreWorkflowVersionMutation,
  useGetWorkflowRunsQuery,
  useGetWorkflowRunQuery,
  useStartWorkflowRunMutation,
  useCancelWorkflowRunMutation,
  useRespondToWorkflowRunMutation,
  useAskWorkflowRunMutation,
  useSendBuilderMessageMutation,
  useLazyGetBuilderTurnQuery,
  useGetApplicationWorkflowsQuery,
  useAddApplicationWorkflowMutation,
  useRemoveApplicationWorkflowMutation,
} = workflowsApi;

/** The message from a failed request, in the shape lumina-api returns it. */
export function workflowErrorMessage(error: unknown): string {
  const data = (error as { data?: Record<string, unknown> } | undefined)?.data;
  if (data && typeof data.message === 'string') return data.message;
  return 'Something went wrong. Try again.';
}

/** The issues a refused save or activation lists, if it listed any. */
export function workflowErrorIssues(error: unknown): ReadinessIssue[] {
  const details = (error as { data?: Record<string, unknown> } | undefined)?.data?.[
    'error-details'
  ];
  return Array.isArray(details) ? (details as ReadinessIssue[]) : [];
}
